import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CurrencyInput } from "@/components/ui/currency-input";
import { ModalShell } from "@/components/ui/modal-shell";
import { useToast } from "@/hooks/use-toast";
import { useFeatureAllowed } from "@/hooks/use-plan";
import { UltraBadge } from "@/components/UltraBadge";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { extractApiErrorMessage } from "@/lib/api-error";
import { formatCurrency } from "@/lib/utils";
import { TicketPercent, Plus, Pencil, Trash2, Loader2, Lock } from "lucide-react";

export interface AddressFilter {
  field: string;
  value: string;
}

export interface Coupon {
  id: string;
  code: string;
  // "free_delivery" (Ultra, 22/09/2026): zera a taxa de entrega; valor fica 0.
  discountType: "percent" | "fixed" | "free_delivery";
  discountValue: string;
  minOrderValue: string | null;
  maxUses: number | null;
  maxUsesPerCustomer: number;
  allowedDays: number[] | null;
  addressFilters: AddressFilter[] | null;
  firstPurchaseOnly?: boolean;
  birthdayOnly?: boolean | null;
  birthdayDaysBefore?: number | null;
  birthdayDaysAfter?: number | null;
  isActive: boolean;
  public?: boolean;
  usesCount: number;
}

// Mesmas chaves e rótulos de stores.manual_address_fields (Settings.tsx) — o
// lojista filtra pelos campos que o cliente realmente vê no link público.
const ADDRESS_FIELD_LABELS: Record<string, string> = {
  cep: "CEP",
  state: "Estado",
  city: "Cidade",
  neighborhood: "Bairro",
  street: "Rua",
  number: "Número",
  complement: "Complemento",
  reference: "Ponto de referência",
};
const ADDRESS_FIELD_ORDER = ["cep", "state", "city", "neighborhood", "street", "number", "complement", "reference"];

const DAY_SHORT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];
const QUERY_KEY = ["coupons"];

function normalizeCode(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 30);
}

function couponSummary(c: Coupon): string {
  const parts: string[] = [];
  // Ordem fixa: uso primeiro, mínimo depois, resto em seguida.
  // "usados" concorda com o total ("3 de 10 cupons usados"); sem teto, com o próprio contador.
  const cupons = (n: number) => (n === 1 ? "cupom usado" : "cupons usados");
  parts.push(
    c.maxUses != null
      ? `${c.usesCount} de ${c.maxUses} ${cupons(c.maxUses)}`
      : `${c.usesCount} ${cupons(c.usesCount)}`,
  );
  const min = c.minOrderValue ? parseFloat(c.minOrderValue) : 0;
  if (min > 0) parts.push(`mín. ${formatCurrency(min)}`);
  parts.push(`${c.maxUsesPerCustomer}x por cliente`);
  if (c.firstPurchaseOnly) parts.push("1ª compra");
  if (c.birthdayOnly) parts.push(`aniversariante (${c.birthdayDaysBefore ?? 3} antes–${c.birthdayDaysAfter ?? 2} depois)`);
  const days = c.allowedDays;
  if (Array.isArray(days) && days.length > 0 && days.length < 7) {
    parts.push([...days].sort((a, b) => a - b).map((d) => DAY_SHORT[d]).join(", "));
  }
  const filters = c.addressFilters;
  if (Array.isArray(filters) && filters.length > 0) {
    parts.push(
      filters.length === 1
        ? `${ADDRESS_FIELD_LABELS[filters[0]!.field] ?? filters[0]!.field}: ${filters[0]!.value}`
        : `${filters.length} filtros de endereço`,
    );
  }
  return parts.join(" • ");
}

export function CouponsSection({
  addressFields,
  footer,
}: {
  addressFields?: Record<string, { enabled: boolean; required: boolean }> | null;
  footer?: React.ReactNode;
}) {
  const { toast } = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const { data: coupons = [], isLoading } = useQuery<Coupon[]>({
    queryKey: QUERY_KEY,
    queryFn: () => customFetch<Coupon[]>("/api/coupons"),
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Coupon | null>(null);
  const [saving, setSaving] = useState(false);

  const [code, setCode] = useState("");
  const [discountType, setDiscountType] = useState<"percent" | "fixed" | "free_delivery">("percent");
  const [percentValue, setPercentValue] = useState("10");
  const [fixedValue, setFixedValue] = useState("");
  const [minOrderValue, setMinOrderValue] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [maxUsesPerCustomer, setMaxUsesPerCustomer] = useState("1");
  const [days, setDays] = useState<number[]>(ALL_DAYS);
  const [filters, setFilters] = useState<AddressFilter[]>([]);
  const [firstPurchaseOnly, setFirstPurchaseOnly] = useState(false);
  // Aniversariante, dias da semana e filtros de endereço são do Ultra
  // (10/09/2026). Cupom antigo mostra o que tem salvo (read-only + selo).
  // "ultra" aqui = pode usar cupom avançado: plano Ultra OU direito da loja
  // (direito adquirido / whitelist do Pedro), que chega como grant no /me.
  const ultra = useFeatureAllowed("coupon_advanced");
  const [mostrarNoLink, setMostrarNoLink] = useState(true);
  const [birthdayOnly, setBirthdayOnly] = useState(false);
  const [birthdayBefore, setBirthdayBefore] = useState(3);
  const [birthdayAfter, setBirthdayAfter] = useState(2);

  // Só oferece o que o cliente vê no checkout: filtrar por campo escondido
  // criaria cupom que nunca casa.
  const filterableFields = ADDRESS_FIELD_ORDER.filter(
    (k) => !addressFields || addressFields[k]?.enabled !== false,
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const openCreate = () => {
    setEditing(null);
    setCode("");
    setDiscountType("percent");
    setPercentValue("10");
    setFixedValue("");
    setMinOrderValue("");
    setMaxUses("");
    setMaxUsesPerCustomer("1");
    setDays(ALL_DAYS);
    setFilters([]);
    // Fora do Ultra o cupom nasce "somente primeira compra" e assim fica;
    // desligar é do Ultra (10/09/2026). Cupom antigo já desligado segue como está.
    setFirstPurchaseOnly(!ultra);
    setMostrarNoLink(true);
    setBirthdayOnly(false);
    setBirthdayBefore(3);
    setBirthdayAfter(2);
    setModalOpen(true);
  };

  const openEdit = (c: Coupon) => {
    setEditing(c);
    setCode(c.code);
    setDiscountType(c.discountType);
    if (c.discountType === "percent") {
      setPercentValue(String(Math.round(parseFloat(c.discountValue))));
      setFixedValue("");
    } else {
      setFixedValue(c.discountValue);
      setPercentValue("10");
    }
    setMinOrderValue(c.minOrderValue ?? "");
    setMaxUses(c.maxUses != null ? String(c.maxUses) : "");
    setMaxUsesPerCustomer(String(c.maxUsesPerCustomer));
    setDays(Array.isArray(c.allowedDays) && c.allowedDays.length > 0 ? c.allowedDays : ALL_DAYS);
    setFilters(Array.isArray(c.addressFilters) ? c.addressFilters : []);
    setFirstPurchaseOnly(!!c.firstPurchaseOnly);
    setMostrarNoLink(c.public !== false);
    setBirthdayOnly(!!c.birthdayOnly);
    setBirthdayBefore(c.birthdayDaysBefore ?? 3);
    setBirthdayAfter(c.birthdayDaysAfter ?? 2);
    setModalOpen(true);
  };

  const toggleDay = (d: number) => {
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  };

  const handleSave = async () => {
    const normalized = normalizeCode(code);
    if (normalized.length < 2) {
      toast({ title: "Informe o código do cupom", description: "Use letras e números, ex: BOASVINDAS10.", variant: "destructive" });
      return;
    }
    // Frete grátis não tem valor: zera a taxa do pedido, seja qual for.
    const value = discountType === "free_delivery"
      ? 0
      : discountType === "percent"
        ? parseInt(percentValue, 10)
        : parseFloat((fixedValue || "0").replace(",", "."));
    if (discountType === "percent" && (!Number.isFinite(value) || value < 1 || value > 100)) {
      toast({ title: "Porcentagem inválida", description: "Use um valor entre 1 e 100.", variant: "destructive" });
      return;
    }
    if (discountType === "fixed" && (!Number.isFinite(value) || value <= 0)) {
      toast({ title: "Informe o valor do desconto", variant: "destructive" });
      return;
    }
    if (days.length === 0) {
      toast({ title: "Selecione ao menos um dia da semana", variant: "destructive" });
      return;
    }
    const perCustomer = parseInt(maxUsesPerCustomer, 10);
    const totalUses = maxUses.trim() === "" ? null : parseInt(maxUses, 10);
    if (totalUses != null && (!Number.isFinite(totalUses) || totalUses < 1)) {
      toast({ title: "Quantidade de cupons inválida", description: "Deixe vazio para ilimitado.", variant: "destructive" });
      return;
    }
    const minVal = minOrderValue.trim() === "" ? null : parseFloat(minOrderValue.replace(",", "."));

    const body = {
      code: normalized,
      discountType,
      discountValue: value,
      minOrderValue: minVal != null && Number.isFinite(minVal) && minVal > 0 ? minVal : null,
      maxUses: totalUses,
      maxUsesPerCustomer: Number.isFinite(perCustomer) && perCustomer >= 1 ? perCustomer : 1,
      allowedDays: days.length === 7 ? null : [...days].sort((a, b) => a - b),
      // Linha sem valor digitado é descartada — senão viraria um filtro que
      // nunca casa e o cupom morreria em silêncio.
      addressFilters: filters.filter((f) => f.field && f.value.trim()).map((f) => ({ field: f.field, value: f.value.trim() })),
      firstPurchaseOnly,
      birthdayOnly,
      birthdayDaysBefore: birthdayOnly ? birthdayBefore : null,
      birthdayDaysAfter: birthdayOnly ? birthdayAfter : null,
      isActive: editing ? editing.isActive : true,
      public: mostrarNoLink,
    };

    setSaving(true);
    try {
      await customFetch(editing ? `/api/coupons/${editing.id}` : "/api/coupons", {
        method: editing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      toast({ title: editing ? "Cupom atualizado!" : "Cupom criado!" });
      setModalOpen(false);
      invalidate();
    } catch (e) {
      toast({ title: "Não foi possível salvar", description: extractApiErrorMessage(e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (c: Coupon, isActive: boolean) => {
    // Otimista: reflete o toggle na hora e reverte se a API falhar.
    queryClient.setQueryData<Coupon[]>(QUERY_KEY, (prev) =>
      (prev ?? []).map((x) => (x.id === c.id ? { ...x, isActive } : x)),
    );
    try {
      await customFetch(`/api/coupons/${c.id}/active`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive }),
      });
    } catch (e) {
      toast({ title: "Não foi possível atualizar", description: extractApiErrorMessage(e), variant: "destructive" });
    } finally {
      invalidate();
    }
  };

  const handleDelete = async (c: Coupon) => {
    if (
      !(await confirm({
        title: `Excluir o cupom ${c.code}?`,
        description: "Clientes não poderão mais usá-lo. Essa ação não pode ser desfeita.",
        confirmLabel: "Excluir",
      }))
    ) {
      return;
    }
    try {
      await customFetch(`/api/coupons/${c.id}`, { method: "DELETE" });
      toast({ title: "Cupom excluído." });
      invalidate();
    } catch (e) {
      toast({ title: "Não foi possível excluir", description: extractApiErrorMessage(e), variant: "destructive" });
    }
  };

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center gap-2 text-lg font-semibold">
        <TicketPercent className="w-5 h-5 text-primary" /> Cupons de Desconto
      </div>
      <p className="text-sm text-muted-foreground">
        Crie códigos como <strong>BOASVINDAS10</strong> pro cliente aplicar no carrinho do link público.
      </p>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando cupons…
        </div>
      ) : coupons.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum cupom ainda. Crie o primeiro!</p>
      ) : (
        <div className="space-y-2.5">
          {coupons.map((c) => (
            <div key={c.id} className={`rounded-xl border border-border bg-card p-3 flex items-center gap-3 ${c.isActive ? "" : "opacity-60"}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold tracking-wider text-sm">{c.code}</span>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                    {c.discountType === "free_delivery"
                      ? "FRETE GRÁTIS"
                      : c.discountType === "percent"
                        ? `${Math.round(parseFloat(c.discountValue))}% OFF`
                        : `-${formatCurrency(parseFloat(c.discountValue))}`}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{couponSummary(c)}</p>
              </div>
              <Switch checked={c.isActive} onCheckedChange={(v) => handleToggleActive(c, v)} />
              <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => openEdit(c)}>
                <Pencil className="w-4 h-4" />
              </Button>
              {/* -ml-2.5 desconta o padding interno dos dois botões: sem isso o
                  vão lápis→lixeira fica maior que o switch→lápis. */}
              <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 -ml-2.5 text-destructive" onClick={() => handleDelete(c)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Button type="button" variant="outline" onClick={openCreate}>
        <Plus className="w-4 h-4 mr-2" /> Criar cupom
      </Button>

      {footer}

      <ModalShell
        open={modalOpen}
        onOpenChange={(open) => { if (!open) setModalOpen(false); }}
        title={editing ? `Editar Cupom ${editing.code}` : "Novo Cupom"}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? "Salvar" : "Criar cupom"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Código do cupom</Label>
            <Input
              value={code}
              onChange={(e) => setCode(normalizeCode(e.target.value))}
              placeholder="EX: BOASVINDAS10"
              className="font-bold tracking-wider uppercase"
              autoFocus={!editing}
            />
            <p className="text-xs text-muted-foreground">Só letras e números, sempre em caixa alta.</p>
          </div>

          <div className="space-y-2">
            <Label>Tipo de desconto</Label>
            <div className="flex border rounded-lg overflow-hidden w-fit">
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${discountType === "percent" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                onClick={() => setDiscountType("percent")}
              >Porcentagem (%)</button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${discountType === "fixed" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                onClick={() => setDiscountType("fixed")}
              >Valor fixo (R$)</button>
              {/* Tipo do Ultra (22/09/2026). Fora do plano fica visível e
                  travado, com o selo — mesma regra dos outros avançados. */}
              <button
                type="button"
                disabled={!ultra}
                className={`px-4 py-2 text-sm font-medium transition-colors ${discountType === "free_delivery" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"} ${ultra ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}
                onClick={() => { if (ultra) setDiscountType("free_delivery"); }}
                data-testid="coupon-type-free-delivery"
              >Frete grátis</button>
            </div>
            {!ultra && <UltraBadge label="Frete grátis — disponível no Ultra" />}
          </div>

          {discountType === "free_delivery" ? (
            <p className="text-xs text-muted-foreground rounded-md border border-border/60 bg-muted/40 p-2">
              Zera a taxa de entrega do pedido. Vale só para entrega — em retirada, ou quando a entrega já é grátis, o cupom não se aplica.
            </p>
          ) : (
          <div className="space-y-2">
            <Label>{discountType === "percent" ? "Desconto (%)" : "Desconto (R$)"}</Label>
            {discountType === "percent" ? (
              <div className="relative max-w-[160px]">
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={100}
                  value={percentValue}
                  onChange={(e) => setPercentValue(e.target.value.replace(/\D/g, ""))}
                  className="pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
              </div>
            ) : (
              <CurrencyInput value={fixedValue} onChange={setFixedValue} className="max-w-[160px]" />
            )}
          </div>
          )}

          <div className="space-y-2">
            <Label>Valor mínimo do carrinho <span className="font-normal text-muted-foreground">(opcional)</span></Label>
            <CurrencyInput value={minOrderValue} onChange={setMinOrderValue} className="max-w-[160px]" placeholder="Sem mínimo" />
          </div>

          {/* Uma coluna só (pedido do Pedro, 21/08): no mobile o grid de 2
              colunas espremia rótulo e toggle. */}
          <div className="space-y-2">
            <Label>Quantidade total de cupons</Label>
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              placeholder="Ilimitado"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value.replace(/\D/g, ""))}
            />
          </div>
          <div className="space-y-2">
            <Label>Usos por cliente</Label>
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              value={firstPurchaseOnly ? "1" : maxUsesPerCustomer}
              disabled={firstPurchaseOnly}
              onChange={(e) => setMaxUsesPerCustomer(e.target.value.replace(/\D/g, ""))}
              onBlur={() => { if (!maxUsesPerCustomer || parseInt(maxUsesPerCustomer, 10) < 1) setMaxUsesPerCustomer("1"); }}
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="coupon-public" className="cursor-pointer leading-tight">Mostrar no link da loja <span className="font-normal text-muted-foreground">— quando a faixa de cupons estiver ligada</span></Label>
            <Switch id="coupon-public" checked={mostrarNoLink} onCheckedChange={setMostrarNoLink} />
          </div>
          {!ultra && firstPurchaseOnly ? (
            // Fora do Ultra o cupom nasce "somente primeira compra" e não desliga.
            // Bloco travado COM a frase do que isso significa: a Entre Sabores
            // (15/09/2026) leu o switch como "1x por cliente" e criou cupom de
            // Dia do Cliente que nenhum cliente antigo conseguia usar.
            <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-3 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="coupon-first-purchase" className="leading-tight inline-flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5 shrink-0 text-amber-700 dark:text-amber-300" /> Somente primeira compra
                </Label>
                <Switch id="coupon-first-purchase" checked disabled />
              </div>
              <p className="text-xs text-amber-800 dark:text-amber-200">Ativado por padrão.</p>
              <UltraBadge label="Só é possível desativar no Plano Ultra" />
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="coupon-first-purchase" className="cursor-pointer leading-tight">Somente primeira compra</Label>
              <Switch
                id="coupon-first-purchase"
                checked={firstPurchaseOnly}
                onCheckedChange={(v) => { setFirstPurchaseOnly(v); if (v) setMaxUsesPerCustomer("1"); }}
              />
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="coupon-birthday-only" className="cursor-pointer leading-tight">
              Somente aniversariantes {!ultra && <UltraBadge className="ml-1 align-middle" />}
            </Label>
            <Switch id="coupon-birthday-only" checked={birthdayOnly} onCheckedChange={setBirthdayOnly} disabled={!ultra} data-testid="coupon-birthday-only" />
          </div>

          {birthdayOnly && (
            <div className="-mt-1.5 space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-14">Vale de</span>
                <select
                  className="border rounded-md px-1.5 py-2 text-sm bg-background h-9"
                  value={String(birthdayBefore)}
                  onChange={(e) => setBirthdayBefore(parseInt(e.target.value, 10))}
                  data-testid="coupon-birthday-before"
                >
                  <option value="0">o dia do aniversário</option>
                  <option value="1">1 dia antes</option>
                  <option value="3">3 dias antes</option>
                  <option value="7">7 dias antes</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-14">até</span>
                <select
                  className="border rounded-md px-1.5 py-2 text-sm bg-background h-9"
                  value={String(birthdayAfter)}
                  onChange={(e) => setBirthdayAfter(parseInt(e.target.value, 10))}
                  data-testid="coupon-birthday-after"
                >
                  <option value="0">o próprio dia</option>
                  <option value="1">1 dia depois</option>
                  <option value="2">2 dias depois</option>
                  <option value="3">3 dias depois</option>
                  <option value="7">7 dias depois</option>
                </select>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Dias da semana em que vale {!ultra && <UltraBadge className="ml-1 align-middle" />}</Label>
            <div className="flex flex-wrap gap-1.5">
              {DAY_SHORT.map((label, d) => {
                const active = days.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    disabled={!ultra}
                    onClick={() => { if (ultra) toggleDay(d); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer border ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground border-border hover:bg-muted"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">Todos marcados = vale em qualquer dia.</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label>Filtros de endereço <span className="font-normal text-muted-foreground">(opcional)</span> {!ultra && <UltraBadge className="ml-1 align-middle" />}</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!ultra}
                onClick={() => setFilters((prev) => [...prev, { field: filterableFields[0] ?? "street", value: "" }])}
                data-testid="coupon-add-filter"
              >
                <Plus className="w-4 h-4 mr-2" /> Adicionar filtro
              </Button>
            </div>
            {filters.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Sem filtro, o cupom vale para qualquer endereço. Com filtro, vale só na entrega e só se o endereço bater.
              </p>
            ) : (
              <div className="space-y-2">
                {filters.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <select
                      value={f.field}
                      disabled={!ultra}
                      onChange={(e) => setFilters((prev) => prev.map((x, j) => (j === i ? { ...x, field: e.target.value } : x)))}
                      // No celular o select comia quase metade da linha e
                      // sobrava um campo de texto de ~4 caracteres. O nome do
                      // campo pode truncar; o que o lojista digita, não.
                      className="border rounded-md px-1.5 py-2 text-sm bg-background h-9 w-24 sm:w-40 shrink-0"
                      data-testid={`coupon-filter-field-${i}`}
                    >
                      {filterableFields.map((k) => (
                        <option key={k} value={k}>{ADDRESS_FIELD_LABELS[k] ?? k}</option>
                      ))}
                    </select>
                    <Input
                      // CEP: mesma máscara do resto do app (exibe 00000-000,
                      // guarda só dígitos — o match do server também compara
                      // por dígitos).
                      value={f.field === "cep" ? f.value.replace(/^(\d{5})(\d{1,3})$/, "$1-$2") : f.value}
                      onChange={(e) => {
                        const v = f.field === "cep" ? e.target.value.replace(/\D/g, "") : e.target.value;
                        setFilters((prev) => prev.map((x, j) => (j === i ? { ...x, value: v } : x)));
                      }}
                      placeholder={f.field === "cep" ? "00000-000" : "contém..."}
                      disabled={!ultra}
                      maxLength={f.field === "cep" ? 9 : undefined}
                      inputMode={f.field === "cep" ? "numeric" : undefined}
                      className="flex-1 min-w-0 h-9"
                      data-testid={`coupon-filter-value-${i}`}
                    />
                    <button
                      type="button"
                      onClick={() => setFilters((prev) => prev.filter((_, j) => j !== i))}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors shrink-0"
                      aria-label="Remover filtro"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground">
                  Basta UM filtro bater. O texto é comparado por "contém", sem diferenciar maiúscula nem acento.
                </p>
              </div>
            )}
          </div>
        </div>
      </ModalShell>
    </Card>
  );
}
